import React, {useState, memo, useCallback, useEffect, useRef} from "react";
import PropTypes from "prop-types";
import {Box, Checkbox, CircularProgress, IconButton, List} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import SessionListHeader from "./SessionListHeader";
import SessionListItem from "./SessionListItem";
import SessionSharingDialog from "./SessionSharingDialog";
import {useRexAITrustedAdvisorContext} from "./Context/RexAITrustedAdvisorProvider";
import {useRexAITrustedAdvisorStyles} from "./useRexAITrustedAdvisorStyles";
import StyledTooltip from "../../components/tooltips/StyledTooltip";
import {ALERT} from "../../resources/environment";
import {includes, map, filter} from "underscore";

const APPROX_ITEM_HEIGHT = 40;
const BUFFER = 5;
const EMPTY_MESSAGES = [];

const SessionList = ({sessionBadges, onCreateNew}) => {
  const {classes} = useRexAITrustedAdvisorStyles();
  const {
    messages,
    sessions,
    sessionId: currentSessionId,
    isCurrentRequest,
    hasMoreSessions,
    isFetchingMoreSessions,
    fetchMoreSessions,
    fetchSessions,
    sessionPageSizeRef,
    loadSession,
    createNewSession,
    deleteSession,
    renameSession,
    setSessionPinned,
    leaveSession,
  } = useRexAITrustedAdvisorContext();

  const [hoveredSession, setHoveredSession] = useState(null);
  const [editingSession, setEditingSession] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [sharingSession, setSharingSession] = useState(null);
  const deletableSessions = filter(sessions, ({shared}) => !shared);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!scrollRef.current) {
      return;
    }

    const updatePageSize = () => {
      const availableHeight = scrollRef.current.clientHeight;
      const pageSize = Math.max(Math.ceil(availableHeight / APPROX_ITEM_HEIGHT) + BUFFER, 10);
      sessionPageSizeRef.current = pageSize;
    };

    updatePageSize();

    const observer = new ResizeObserver(updatePageSize);
    observer.observe(scrollRef.current);
    return () => observer.disconnect();
  }, [sessionPageSizeRef]);

  const fetchMoreRef = useRef(fetchMoreSessions);
  fetchMoreRef.current = fetchMoreSessions;

  useEffect(() => {
    if (!sentinelRef.current || !hasMoreSessions || !scrollRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetchMoreRef.current();
        }
      },
      {
        root: scrollRef.current,
        threshold: 0,
        rootMargin: `0px 0px ${APPROX_ITEM_HEIGHT * BUFFER}px 0px`,
      },
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMoreSessions]);

  const handleLoadSession = useCallback(
    (event) => {
      const sessionId = event.currentTarget.dataset.sessionId;
      const isActiveStreamingSession = isCurrentRequest && sessionId === currentSessionId;
      if (editingSession !== sessionId && !isActiveStreamingSession) {
        loadSession(sessionId);
      }
    },
    [currentSessionId, editingSession, isCurrentRequest, loadSession],
  );

  const handleDelete = useCallback(
    (event, sessionId) => {
      event.stopPropagation();
      deleteSession(sessionId);
    },
    [deleteSession],
  );

  const handleStartRename = useCallback((sessionId) => {
    setEditingSession(sessionId);
  }, []);

  const handleRenameComplete = useCallback(
    (sessionId, newName) => {
      if (newName) {
        renameSession(sessionId, newName);
      }
      setEditingSession(null);
    },
    [renameSession],
  );

  const handleHover = useCallback((sessionId) => setHoveredSession(sessionId), []);
  const handleLeave = useCallback(() => setHoveredSession(null), []);

  const handleToggleSelect = useCallback((sessionId) => {
    setSelectedIds((prev) =>
      includes(prev, sessionId) ? filter(prev, (id) => id !== sessionId) : [...prev, sessionId],
    );
  }, []);

  const handleToggleAll = useCallback(() => {
    setSelectedIds((prev) =>
      prev.length === deletableSessions.length ?
        [] :
        map(deletableSessions, (session) => session.sessionId),
    );
  }, [deletableSessions]);

  const handleBulkDelete = useCallback(async () => {
    await Promise.allSettled(map(selectedIds, (sessionId) => deleteSession(sessionId)));
    setSelectedIds([]);
    setSelectMode(false);
  }, [selectedIds, deleteSession]);

  const handleExitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedIds([]);
  }, []);

  const handleEnterSelectMode = useCallback(() => setSelectMode(true), []);

  const handleCopySessionId = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(currentSessionId);
      ALERT.success("Session ID copied to clipboard");
    } catch {
      ALERT.error("Could not copy the session ID");
    }
  }, [currentSessionId]);

  return (
    <Box className={classes.sessionListContainer}>
      <SessionListHeader onCreateNew={onCreateNew ?? createNewSession} />

      <Box className={classes.selectModeBar}>
        {selectMode ?
          (
            <>
              <Checkbox
                size="small"
                indeterminate={
                  selectedIds.length > 0 && selectedIds.length < deletableSessions.length
                }
                checked={
                  selectedIds.length === deletableSessions.length && deletableSessions.length > 0
                }
                onChange={handleToggleAll}
              />
              <StyledTooltip title="Delete selected">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={selectedIds.length === 0}
                    onClick={handleBulkDelete}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </StyledTooltip>
              <StyledTooltip title="Cancel">
                <IconButton size="small" onClick={handleExitSelectMode}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </StyledTooltip>
            </>
          ) :
          (
            <>
              <StyledTooltip title="Select sessions to bulk delete">
                <IconButton
                  size="small"
                  onClick={handleEnterSelectMode}
                  disabled={deletableSessions.length === 0}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </StyledTooltip>
              <StyledTooltip
                title={currentSessionId ? "Copy session ID" : "Open a session to copy its ID"}
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={handleCopySessionId}
                    disabled={!currentSessionId}
                    aria-label="Copy session ID"
                  >
                    <FingerprintIcon fontSize="small" />
                  </IconButton>
                </span>
              </StyledTooltip>
            </>
          )}
      </Box>

      <List ref={scrollRef} className={classes.sessionList}>
        {map(sessions, (session, index) => (
          <SessionListItem
            key={`${session.sessionId}-${index}`}
            session={session}
            isSelected={session.sessionId === currentSessionId}
            hasBadge={includes(sessionBadges, session.sessionId)}
            isEditing={editingSession === session.sessionId}
            isHovered={hoveredSession === session.sessionId}
            className={classes.sessionListItem}
            onLoad={selectMode ? undefined : handleLoadSession}
            onHover={handleHover}
            onLeave={handleLeave}
            onRenameComplete={handleRenameComplete}
            onStartRename={handleStartRename}
            onDelete={handleDelete}
            onShare={setSharingSession}
            messages={session.sessionId === currentSessionId ? messages : EMPTY_MESSAGES}
            onTogglePin={setSessionPinned}
            onLeaveSession={leaveSession}
            selectMode={selectMode && !session.shared}
            isChecked={includes(selectedIds, session.sessionId)}
            onToggleSelect={handleToggleSelect}
          />
        ))}
        {hasMoreSessions && (
          <Box ref={sentinelRef} display="flex" justifyContent="center" py={1}>
            {isFetchingMoreSessions && <CircularProgress size={20} />}
          </Box>
        )}
      </List>
      {sharingSession && (
        <SessionSharingDialog
          open
          session={sharingSession}
          onClose={() => setSharingSession(null)}
          onChanged={fetchSessions}
        />
      )}
    </Box>
  );
};

SessionList.propTypes = {
  sessionBadges: PropTypes.array,
  onCreateNew: PropTypes.func,
};

export default memo(SessionList);
